import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { Tabs, TabsList, TabsTrigger } from './ui/tabs';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';
import {
  ListTodo,
  Plus,
  Calendar,
  Clock,
  User,
  CheckCircle,
  Timer,
  Users,
  Check,
  X,
  Eye,
  RefreshCw,
  Edit,
  Trash2,
  MoreVertical,
  Settings,
  SlidersHorizontal,
  Bot,
  Trash,
  Printer
} from 'lucide-react';
import { toast } from 'sonner';
import { escapeDeep } from '../lib/escapeHtml';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEFAULT_SLOTS = [
  { slot: '9am', label: 'Morning', time: '9:00 AM' },
  { slot: '1pm', label: 'Afternoon', time: '1:00 PM' },
  { slot: '9pm', label: 'Night', time: '9:00 PM' },
];

const StatusBadge = ({ chore }) => {
  const verified = chore.status === 'completed' && chore.verified_by_staff;
  const map = {
    verified: { cls: 'bg-green-100 text-green-800 border-green-200', Icon: CheckCircle, text: 'Verified' },
    completed: { cls: 'bg-blue-100 text-blue-800 border-blue-200', Icon: Check, text: 'Completed' },
    skipped: { cls: 'bg-gray-100 text-gray-700 border-gray-200', Icon: X, text: 'Skipped' },
    pending: { cls: 'bg-yellow-100 text-yellow-800 border-yellow-200', Icon: Clock, text: 'Pending' },
  };
  const key = verified ? 'verified' : chore.status === 'completed' ? 'completed' : chore.status === 'skipped' ? 'skipped' : 'pending';
  const { cls, Icon, text } = map[key];
  return (
    <Badge className={`px-2 py-1 ${cls}`} data-testid={`chore-status-${chore.id}`}>
      <Icon className="h-3 w-3 mr-1" />
      {text}
    </Badge>
  );
};

const ChoreManagement = () => {
  const { user } = useAuth();
  const [chores, setChores] = useState([]);
  const [residents, setResidents] = useState([]);
  const [slots, setSlots] = useState(DEFAULT_SLOTS);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [activeTab, setActiveTab] = useState('week');

  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);
  const [showClearDialog, setShowClearDialog] = useState(false);
  const [showAdminDialog, setShowAdminDialog] = useState(false);
  const [showSlotDialog, setShowSlotDialog] = useState(false);

  const [selectedChore, setSelectedChore] = useState(null);
  const [editingChore, setEditingChore] = useState(null);
  const [assignmentLoading, setAssignmentLoading] = useState(false);

  const emptyForm = {
    title: '',
    description: '',
    assigned_resident_id: '',
    time_slots: ['9am'],
    date: new Date().toISOString().split('T')[0],
    location: '',
    estimated_duration: 30,
    notes: '',
    is_recurring: true,
  };
  const [formData, setFormData] = useState(emptyForm);
  const [editFormData, setEditFormData] = useState({
    title: '', description: '', assigned_resident_id: '', time_slot: '9am',
    date: '', location: '', estimated_duration: 30, notes: '',
  });
  const [completeData, setCompleteData] = useState({
    completed_by_resident: '', completed_by_resident_id: '', notes: '',
  });
  const [slotForm, setSlotForm] = useState(DEFAULT_SLOTS);

  const slotLabel = useCallback((slotValue) => {
    const s = slots.find((x) => x.slot === slotValue);
    return s ? `${s.label} (${s.time})` : slotValue;
  }, [slots]);

  const residentDisplay = (r) => `${r.first_name} ${r.last_name}${r.room_number ? ` · Room ${r.room_number}` : ''}`;

  const fetchChores = useCallback(async () => {
    try {
      setLoading(true);
      const url = activeTab === 'week' ? `${API}/chores` : `${API}/chores?date=${selectedDate}`;
      const response = await axios.get(url);
      if (activeTab === 'week') {
        const today = new Date(); today.setHours(0, 0, 0, 0);
        const nextWeek = new Date(today); nextWeek.setDate(today.getDate() + 7);
        setChores(response.data.filter((c) => {
          const d = new Date(c.date + 'T00:00:00');
          return d >= today && d <= nextWeek;
        }));
      } else {
        setChores(response.data);
      }
    } catch (e) {
      console.error('Error fetching chores:', e);
      toast.error('Failed to load chores');
    } finally {
      setLoading(false);
    }
  }, [activeTab, selectedDate]);

  const fetchResidents = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/residents?status=current`);
      setResidents(response.data);
    } catch (e) {
      console.error('Error fetching residents:', e);
    }
  }, []);

  const fetchSlots = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/chore-settings`);
      if (response.data?.slots?.length) setSlots(response.data.slots);
    } catch (e) {
      console.error('Error fetching chore settings:', e);
    }
  }, []);

  useEffect(() => { fetchChores(); }, [fetchChores]);
  useEffect(() => { fetchResidents(); fetchSlots(); }, [fetchResidents, fetchSlots]);

  // Which resident ids are already booked in a given date + slot (from loaded chores)
  const bookedIds = useCallback((date, slot) => {
    return new Set(
      chores
        .filter((c) => c.date === date && c.time_slot === slot && c.assigned_resident_id)
        .map((c) => c.assigned_resident_id)
    );
  }, [chores]);

  // Residents available for the add form: not booked in ANY selected slot on the chosen date
  const availableForCreate = residents.filter((r) => {
    if (!formData.time_slots.length) return true;
    return !formData.time_slots.some((slot) => bookedIds(formData.date, slot).has(r.id));
  });

  const availableForEdit = residents.filter((r) => {
    if (!editingChore) return true;
    if (r.id === editingChore.assigned_resident_id) return true;
    return !bookedIds(editFormData.date, editFormData.time_slot).has(r.id);
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.assigned_resident_id) { toast.error('Please select a resident'); return; }
    if (formData.time_slots.length === 0) { toast.error('Please select at least one time slot'); return; }
    const resident = residents.find((r) => r.id === formData.assigned_resident_id);
    const residentName = resident ? `${resident.first_name} ${resident.last_name}` : '';
    try {
      for (const slot of formData.time_slots) {
        const payload = {
          title: formData.title,
          description: formData.description,
          assigned_resident_id: formData.assigned_resident_id,
          assigned_to_resident: residentName,
          time_slot: slot,
          date: formData.date,
          location: formData.location,
          estimated_duration: formData.estimated_duration,
          notes: formData.notes,
          is_recurring: formData.is_recurring,
        };
        await axios.post(`${API}/chores`, payload);
      }
      const labels = formData.time_slots.map(slotLabel).join(', ');
      toast.success(`Chore assigned to ${residentName} for ${labels}`);
      setFormData(emptyForm);
      setShowAddDialog(false);
      fetchChores();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to create chore';
      toast.error(typeof msg === 'string' ? msg : 'Failed to create chore');
    }
  };

  const openEditDialog = (chore) => {
    setEditingChore(chore);
    setEditFormData({
      title: chore.title,
      description: chore.description || '',
      assigned_resident_id: chore.assigned_resident_id || '',
      time_slot: chore.time_slot,
      date: chore.date,
      location: chore.location || '',
      estimated_duration: chore.estimated_duration || 30,
      notes: chore.notes || '',
    });
    setShowEditDialog(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editingChore) return;
    const resident = residents.find((r) => r.id === editFormData.assigned_resident_id);
    try {
      await axios.put(`${API}/chores/${editingChore.id}`, {
        title: editFormData.title,
        description: editFormData.description,
        assigned_resident_id: editFormData.assigned_resident_id,
        assigned_to_resident: resident ? `${resident.first_name} ${resident.last_name}` : editingChore.assigned_to_resident,
        time_slot: editFormData.time_slot,
        date: editFormData.date,
        location: editFormData.location,
        estimated_duration: editFormData.estimated_duration,
        notes: editFormData.notes,
        is_recurring: false,
      });
      toast.success('Chore updated successfully');
      setShowEditDialog(false);
      setEditingChore(null);
      fetchChores();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to update chore';
      toast.error(typeof msg === 'string' ? msg : 'Failed to update chore');
    }
  };

  const handleDeleteChore = async (choreId, choreTitle) => {
    if (!window.confirm(`Delete the chore "${choreTitle}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/chores/${choreId}`);
      toast.success('Chore deleted');
      fetchChores();
    } catch (error) {
      toast.error(error.response?.status === 403 ? 'Only administrators can delete chores' : 'Failed to delete chore');
    }
  };

  const openCompleteDialog = (chore) => {
    setSelectedChore(chore);
    setCompleteData({
      completed_by_resident: chore.assigned_to_resident || '',
      completed_by_resident_id: chore.assigned_resident_id || '',
      notes: '',
    });
    setShowCompleteDialog(true);
  };

  const handleCompleteChore = async (e) => {
    e.preventDefault();
    if (!selectedChore) return;
    try {
      await axios.put(`${API}/chores/${selectedChore.id}/complete`, completeData);
      toast.success('Chore marked as completed');
      setShowCompleteDialog(false);
      setSelectedChore(null);
      fetchChores();
    } catch (error) {
      toast.error('Failed to complete chore');
    }
  };

  const handleVerifyChore = async (choreId) => {
    try {
      await axios.put(`${API}/chores/${choreId}/verify`, { verified_by_staff: user.full_name, verified_by_staff_id: user.id });
      toast.success('Chore verified by staff');
      fetchChores();
    } catch (error) { toast.error('Failed to verify chore'); }
  };

  const handleSkipChore = async (choreId) => {
    try {
      await axios.put(`${API}/chores/${choreId}/skip`);
      toast.success('Chore marked as skipped');
      fetchChores();
    } catch (error) { toast.error('Failed to skip chore'); }
  };

  const handleTimeSlotToggle = (slotValue) => {
    setFormData((prev) => {
      const has = prev.time_slots.includes(slotValue);
      return { ...prev, time_slots: has ? prev.time_slots.filter((s) => s !== slotValue) : [...prev.time_slots, slotValue] };
    });
  };

  // Admin / assignment controls
  const handleTriggerWeeklyAssignment = async () => {
    if (!window.confirm('Trigger weekly auto-assignment? This assigns chores to residents (Mon–Sun) across the 3 daily slots.')) return;
    try {
      setAssignmentLoading(true);
      const response = await axios.post(`${API}/assignments/weekly-auto-assign`);
      const created = response.data.chores_created ?? 0;
      if (created > 0) {
        toast.success(`Weekly auto-assignment created ${created} chore assignment${created !== 1 ? 's' : ''}.`);
      } else if ((response.data.residents_count ?? 0) === 0) {
        toast.warning('No current residents to assign chores to. Add residents first.');
      } else {
        toast.warning('No new chore assignments were created.');
      }
      setShowAdminDialog(false);
      fetchChores();
    } catch (error) {
      toast.error(error.response?.status === 403 ? 'Staff or admin privileges required' : 'Failed to trigger weekly assignments');
    } finally { setAssignmentLoading(false); }
  };

  const confirmClearAllAssignments = async () => {
    try {
      setAssignmentLoading(true);
      const response = await axios.post(`${API}/assignments/clear-all`);
      toast.success(`Cleared ${response.data.chores_cleared} chores and ${response.data.laundry_sessions_cleared} laundry sessions`);
      setShowClearDialog(false);
      fetchChores();
    } catch (error) {
      toast.error(error.response?.status === 403 ? 'Only administrators can clear assignments' : 'Failed to clear assignments');
      setShowClearDialog(false);
    } finally { setAssignmentLoading(false); }
  };

  // Slot settings
  const openSlotDialog = () => { setSlotForm(slots.map((s) => ({ ...s }))); setShowSlotDialog(true); };
  const handleSaveSlots = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.put(`${API}/chore-settings`, { slots: slotForm });
      setSlots(response.data.slots);
      toast.success('Time slot names updated');
      setShowSlotDialog(false);
    } catch (error) {
      toast.error(error.response?.status === 403 ? 'Staff privileges required' : 'Failed to update slots');
    }
  };

  const printSchedule = () => {
    const renderDay = (dateStr, rawDayChores) => {
      const dayChores = escapeDeep(rawDayChores || []);
      const displayDate = new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
      const rows = slots.map((s) => {
        const slotChores = dayChores.filter((c) => c.time_slot === s.slot);
        const items = slotChores.length === 0
          ? '<div style="color:#666;margin:8px;"><em>No chores scheduled</em></div>'
          : slotChores.map((c) => `
              <div style="border:1px solid #ddd;margin:8px 0;padding:10px;">
                <div style="font-weight:bold;color:#4f46e5;">${c.assigned_to_resident || 'Unassigned'}</div>
                <div style="font-weight:600;">${c.title}</div>
                ${c.location ? `<div style="font-size:13px;color:#374151;">Location: ${c.location}</div>` : ''}
                <div style="font-size:12px;color:#374151;">Status: ${c.status.toUpperCase()}${c.completed_by_resident ? ` — by ${c.completed_by_resident}` : ''}</div>
              </div>`).join('');
        return `<div style="margin:16px 0;"><div style="background:#f3f4f6;padding:8px;font-weight:bold;border-left:4px solid #4f46e5;">${s.label} — ${s.time} (${slotChores.length})</div>${items}</div>`;
      }).join('');
      return `<section style="page-break-after:always;"><h2 style="border-bottom:2px solid #333;padding-bottom:6px;">${displayDate}</h2>${rows}</section>`;
    };

    let title;
    let sections;
    if (activeTab === 'week') {
      title = 'Weekly Chore Schedule';
      sections = sortedDates.length
        ? sortedDates.map((d) => renderDay(d, choresByDate[d])).join('')
        : '<p style="text-align:center;color:#666;">No chores scheduled this week.</p>';
    } else {
      title = 'Daily Chore Schedule';
      sections = renderDay(selectedDate, chores.filter((c) => c.date === selectedDate));
    }

    const html = `<!DOCTYPE html><html><head><title>${title}</title></head><body style="font-family:Arial,sans-serif;margin:20px;"><div style="text-align:center;"><h1>${title}</h1></div>${sections}<div style="margin-top:24px;text-align:center;font-size:12px;color:#666;">Printed ${new Date().toLocaleString()}</div></body></html>`;
    const w = window.open('', '_blank');
    w.document.write(html);
    w.document.close();
    w.print();
  };

  const choresByDate = chores.reduce((acc, c) => { (acc[c.date] = acc[c.date] || []).push(c); return acc; }, {});
  const sortedDates = Object.keys(choresByDate).sort();
  const choresByTimeSlot = chores.reduce((acc, c) => { (acc[c.time_slot] = acc[c.time_slot] || []).push(c); return acc; }, {});

  if (loading) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold text-gray-900">Chore Management</h1>
        <div className="grid grid-cols-1 gap-6">
          {[...Array(3)].map((_, i) => (
            <Card key={i} className="card-shadow"><CardContent className="p-6"><div className="skeleton h-32 w-full rounded" /></CardContent></Card>
          ))}
        </div>
      </div>
    );
  }

  const renderChoreCard = (chore) => (
    <Card key={chore.id} className="border-l-4 border-indigo-400" data-testid={`chore-card-${chore.id}`}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center space-x-2">
            <User className="h-4 w-4 text-indigo-600" />
            <span data-testid={`chore-resident-${chore.id}`}>{chore.assigned_to_resident || 'Unassigned'}</span>
            {chore.description?.includes('auto-assigned') || chore.description?.includes('— assigned to') ? (
              <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                <Bot className="h-3 w-3 mr-1" />Auto
              </Badge>
            ) : null}
          </CardTitle>
          <div className="flex items-center space-x-2">
            <StatusBadge chore={chore} />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`chore-menu-${chore.id}`}>
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => openEditDialog(chore)} data-testid={`chore-edit-${chore.id}`}>
                  <Edit className="h-4 w-4 mr-2" />Edit Chore
                </DropdownMenuItem>
                {user?.role === 'admin' && (
                  <DropdownMenuItem onClick={() => handleDeleteChore(chore.id, chore.title)} className="text-red-600 focus:text-red-600" data-testid={`chore-delete-${chore.id}`}>
                    <Trash2 className="h-4 w-4 mr-2" />Delete Chore
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        <CardDescription className="font-semibold text-gray-900">{chore.title}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {chore.description && <p className="text-sm text-gray-600">{chore.description}</p>}
        {chore.estimated_duration && (
          <div className="flex items-center space-x-2 text-sm text-gray-600"><Timer className="h-4 w-4" /><span>{chore.estimated_duration} minutes</span></div>
        )}
        {chore.status === 'completed' && (
          <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center space-x-2 mb-1">
              <User className="h-4 w-4 text-green-600" />
              <span className="text-sm font-medium text-green-900">Completed by: {chore.completed_by_resident}</span>
            </div>
            {chore.verified_by_staff && (
              <div className="flex items-center space-x-2"><Eye className="h-4 w-4 text-green-600" /><span className="text-sm text-green-700">Verified by: {chore.verified_by_staff}</span></div>
            )}
          </div>
        )}
        <div className="flex space-x-2 pt-1">
          {chore.status === 'pending' && (
            <>
              <Button onClick={() => openCompleteDialog(chore)} size="sm" className="flex-1 bg-green-600 hover:bg-green-700" data-testid={`chore-complete-${chore.id}`}>
                <Check className="h-3 w-3 mr-1" />Complete
              </Button>
              <Button onClick={() => handleSkipChore(chore.id)} size="sm" variant="outline" className="text-gray-600" data-testid={`chore-skip-${chore.id}`}>
                <X className="h-3 w-3 mr-1" />Skip
              </Button>
            </>
          )}
          {chore.status === 'completed' && !chore.verified_by_staff && (
            <Button onClick={() => handleVerifyChore(chore.id)} size="sm" className="flex-1 bg-blue-600 hover:bg-blue-700" data-testid={`chore-verify-${chore.id}`}>
              <Eye className="h-3 w-3 mr-1" />Verify
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6 fade-in" data-testid="chore-management">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center space-x-3">
            <ListTodo className="h-8 w-8 text-indigo-600" />
            <span>Chore Management</span>
          </h1>
          <p className="text-gray-600 mt-1">Resident chore assignments — weekly, {slots.length} daily time slots</p>
        </div>

        <div className="mt-4 sm:mt-0 flex flex-wrap items-center gap-2">
          <Button onClick={fetchChores} variant="outline" size="sm" data-testid="refresh-chores-btn">
            <RefreshCw className="h-4 w-4 mr-2" />Refresh
          </Button>
          <Button onClick={openSlotDialog} variant="outline" size="sm" className="border-indigo-200 text-indigo-700 hover:bg-indigo-50" data-testid="slot-settings-btn">
            <SlidersHorizontal className="h-4 w-4 mr-2" />Slot Settings
          </Button>
          <Button onClick={printSchedule} variant="outline" size="sm" className="border-green-200 text-green-700 hover:bg-green-50" data-testid="print-schedule-btn">
            <Printer className="h-4 w-4 mr-2" />{activeTab === 'week' ? 'Print Week' : 'Print Day'}
          </Button>

          {user?.role === 'admin' && (
            <Dialog open={showAdminDialog} onOpenChange={setShowAdminDialog}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" className="border-indigo-200 text-indigo-700 hover:bg-indigo-50" data-testid="admin-controls-btn">
                  <Settings className="h-4 w-4 mr-2" />Admin Controls
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="flex items-center space-x-2"><Bot className="h-5 w-5 text-indigo-600" /><span>Assignment Management</span></DialogTitle>
                  <DialogDescription>Automated resident chore assignments</DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <h3 className="font-medium text-blue-900 mb-2">Weekly Auto-Assignment</h3>
                    <p className="text-sm text-blue-700 mb-3">Rotates current residents across chores for each daily slot, Monday–Sunday. Skips residents already booked in a slot. Runs automatically every Sunday at 9 PM.</p>
                    <Button onClick={handleTriggerWeeklyAssignment} disabled={assignmentLoading} className="w-full bg-green-600 hover:bg-green-700 text-white" data-testid="trigger-weekly-btn">
                      {assignmentLoading ? (<><Timer className="h-4 w-4 mr-2 animate-spin" />Assigning...</>) : (<><Bot className="h-4 w-4 mr-2" />Trigger Weekly Assignment</>)}
                    </Button>
                  </div>
                  <div className="p-4 bg-red-50 rounded-lg">
                    <h3 className="font-medium text-red-900 mb-2">Clear All Assignments</h3>
                    <p className="text-sm text-red-700 mb-3">Remove all chores and laundry assignments. This cannot be undone.</p>
                    <Button onClick={() => setShowClearDialog(true)} disabled={assignmentLoading} variant="destructive" className="w-full" data-testid="clear-all-btn">
                      <Trash className="h-4 w-4 mr-2" />Clear All Assignments
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          )}

          <Dialog open={showAddDialog} onOpenChange={(o) => { setShowAddDialog(o); if (o) setFormData(emptyForm); }}>
            <DialogTrigger asChild>
              <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="add-chore-btn">
                <Plus className="h-4 w-4 mr-2" />Add Chore
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Assign a Chore to a Resident</DialogTitle>
                <DialogDescription>Pick a resident and one or more daily time slots. Residents already booked in a slot are hidden.</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="text-sm font-medium text-gray-700">Chore Title *</label>
                    <Input required value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} placeholder="e.g., Clean bathroom" data-testid="chore-title-input" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-sm font-medium text-gray-700">Description</label>
                    <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Details of the chore..." rows={2} data-testid="chore-description-input" />
                  </div>

                  <div className="md:col-span-2">
                    <label className="text-sm font-medium text-gray-700">Assign to Resident *</label>
                    <Select value={formData.assigned_resident_id} onValueChange={(v) => setFormData({ ...formData, assigned_resident_id: v })}>
                      <SelectTrigger data-testid="chore-resident-select"><SelectValue placeholder="Select resident" /></SelectTrigger>
                      <SelectContent>
                        {availableForCreate.length === 0 ? (
                          <SelectItem value="none" disabled>No available residents for the selected slot(s)</SelectItem>
                        ) : availableForCreate.map((r) => (
                          <SelectItem key={r.id} value={r.id} data-testid={`resident-option-${r.id}`}>{residentDisplay(r)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="md:col-span-2">
                    <label className="text-sm font-medium text-gray-700">Time Slots * (select all that apply)</label>
                    <div className="mt-2 space-y-2">
                      {slots.map((s) => (
                        <div key={s.slot} className="flex items-center space-x-2">
                          <Checkbox id={`slot-${s.slot}`} checked={formData.time_slots.includes(s.slot)} onCheckedChange={() => handleTimeSlotToggle(s.slot)} data-testid={`slot-checkbox-${s.slot}`} />
                          <label htmlFor={`slot-${s.slot}`} className="text-sm text-gray-700 cursor-pointer">{s.label} — {s.time}</label>
                        </div>
                      ))}
                    </div>
                    {formData.time_slots.length === 0 && <p className="text-xs text-red-600 mt-1">Select at least one time slot</p>}
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700">Start Date *</label>
                    <Input type="date" required value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} data-testid="chore-date-input" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Duration (minutes)</label>
                    <Input type="number" value={formData.estimated_duration} onChange={(e) => setFormData({ ...formData, estimated_duration: parseInt(e.target.value) || 0 })} min="5" max="120" data-testid="chore-duration-input" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-sm font-medium text-gray-700">Location/Area</label>
                    <Input value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })} placeholder="e.g., Main bathroom" data-testid="chore-location-input" />
                  </div>
                </div>

                <div className="flex items-center space-x-2 p-3 bg-blue-50 rounded-lg">
                  <Checkbox id="recurring" checked={formData.is_recurring} onCheckedChange={(v) => setFormData({ ...formData, is_recurring: !!v })} data-testid="chore-recurring-checkbox" />
                  <label htmlFor="recurring" className="text-sm font-medium text-blue-900 cursor-pointer">Recurring — repeat this assignment for the next 7 days</label>
                </div>

                <div className="flex justify-end space-x-3">
                  <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
                  <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" data-testid="submit-chore-btn">Create Chore</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Tabs + date */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full sm:w-auto">
          <TabsList>
            <TabsTrigger value="week" data-testid="tab-week">This Week</TabsTrigger>
            <TabsTrigger value="date" data-testid="tab-date">Select Date</TabsTrigger>
          </TabsList>
        </Tabs>
        {activeTab === 'date' && (
          <div className="flex items-center space-x-2">
            <Calendar className="h-4 w-4 text-gray-500" />
            <Input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} className="w-auto" data-testid="select-date-input" />
          </div>
        )}
      </div>

      {/* Body */}
      <div className="space-y-6">
        {activeTab === 'week' ? (
          sortedDates.length === 0 ? (
            <Card className="card-shadow">
              <CardContent className="text-center py-8">
                <ListTodo className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No Chores This Week</h3>
                <p className="text-gray-500 mb-4">Add a chore or use the weekly auto-assignment to create resident assignments.</p>
              </CardContent>
            </Card>
          ) : (
            sortedDates.map((date) => {
              const dayChores = choresByDate[date];
              const dayName = new Date(date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
              return (
                <Card key={date} className="card-shadow" data-testid={`day-card-${date}`}>
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                      <div className="flex items-center space-x-3"><Calendar className="h-5 w-5 text-indigo-600" /><span>{dayName}</span></div>
                      <Badge variant="outline">{dayChores.length} chore{dayChores.length !== 1 ? 's' : ''}</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {slots.map((s) => {
                      const slotChores = dayChores.filter((c) => c.time_slot === s.slot);
                      if (slotChores.length === 0) return null;
                      return (
                        <div key={s.slot} className="border rounded-lg p-4">
                          <div className="flex items-center space-x-2 mb-3">
                            <Clock className="h-4 w-4 text-gray-600" />
                            <span className="font-medium text-gray-900">{s.label} — {s.time}</span>
                            <Badge variant="secondary">{slotChores.length}</Badge>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {slotChores.map(renderChoreCard)}
                          </div>
                        </div>
                      );
                    })}
                  </CardContent>
                </Card>
              );
            })
          )
        ) : (
          slots.map((s) => {
            const slotChores = choresByTimeSlot[s.slot] || [];
            return (
              <Card key={s.slot} className="card-shadow" data-testid={`slot-card-${s.slot}`}>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center space-x-3"><Clock className="h-5 w-5 text-indigo-600" /><span>{s.label} — {s.time}</span></div>
                    <Badge variant="outline">{slotChores.length} chore{slotChores.length !== 1 ? 's' : ''}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {slotChores.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <ListTodo className="h-12 w-12 mx-auto mb-2 opacity-50" />
                      <p>No chores scheduled for this slot</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">{slotChores.map(renderChoreCard)}</div>
                  )}
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Complete Dialog */}
      <Dialog open={showCompleteDialog} onOpenChange={setShowCompleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Complete Chore</DialogTitle>
            <DialogDescription>{selectedChore?.title} — confirm who completed this chore</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCompleteChore} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Completed by *</label>
              <Select value={completeData.completed_by_resident_id} onValueChange={(v) => {
                const r = residents.find((x) => x.id === v);
                setCompleteData({ ...completeData, completed_by_resident_id: v, completed_by_resident: r ? `${r.first_name} ${r.last_name}` : '' });
              }}>
                <SelectTrigger data-testid="complete-resident-select"><SelectValue placeholder="Select resident" /></SelectTrigger>
                <SelectContent>
                  {residents.map((r) => (<SelectItem key={r.id} value={r.id}>{residentDisplay(r)}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-800">
              <strong>Chore:</strong> {selectedChore?.title}<br />
              <strong>Assigned to:</strong> {selectedChore?.assigned_to_resident || '—'}<br />
              <strong>Slot:</strong> {selectedChore ? slotLabel(selectedChore.time_slot) : ''}
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Completion Notes (optional)</label>
              <Textarea value={completeData.notes} onChange={(e) => setCompleteData({ ...completeData, notes: e.target.value })} rows={2} data-testid="complete-notes-input" />
            </div>
            <div className="flex justify-end space-x-3">
              <Button type="button" variant="outline" onClick={() => setShowCompleteDialog(false)}>Cancel</Button>
              <Button type="submit" className="bg-green-600 hover:bg-green-700" disabled={!completeData.completed_by_resident_id} data-testid="confirm-complete-btn">Mark as Complete</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Chore</DialogTitle>
            <DialogDescription>Update chore details, resident, or time slot</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="text-sm font-medium text-gray-700">Chore Title *</label>
                <Input required value={editFormData.title} onChange={(e) => setEditFormData({ ...editFormData, title: e.target.value })} data-testid="edit-chore-title-input" />
              </div>
              <div className="md:col-span-2">
                <label className="text-sm font-medium text-gray-700">Description</label>
                <Textarea value={editFormData.description} onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })} rows={2} />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Assigned Resident *</label>
                <Select value={editFormData.assigned_resident_id} onValueChange={(v) => setEditFormData({ ...editFormData, assigned_resident_id: v })}>
                  <SelectTrigger data-testid="edit-chore-resident-select"><SelectValue placeholder="Select resident" /></SelectTrigger>
                  <SelectContent>
                    {availableForEdit.map((r) => (<SelectItem key={r.id} value={r.id}>{residentDisplay(r)}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Time Slot *</label>
                <Select value={editFormData.time_slot} onValueChange={(v) => setEditFormData({ ...editFormData, time_slot: v })}>
                  <SelectTrigger data-testid="edit-chore-slot-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {slots.map((s) => (<SelectItem key={s.slot} value={s.slot}>{s.label} — {s.time}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Date *</label>
                <Input type="date" required value={editFormData.date} onChange={(e) => setEditFormData({ ...editFormData, date: e.target.value })} />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Duration (minutes)</label>
                <Input type="number" value={editFormData.estimated_duration} onChange={(e) => setEditFormData({ ...editFormData, estimated_duration: parseInt(e.target.value) || 0 })} min="5" max="120" />
              </div>
              <div className="md:col-span-2">
                <label className="text-sm font-medium text-gray-700">Location/Area</label>
                <Input value={editFormData.location} onChange={(e) => setEditFormData({ ...editFormData, location: e.target.value })} />
              </div>
            </div>
            <div className="flex justify-end space-x-3">
              <Button type="button" variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
              <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" data-testid="update-chore-btn">Update Chore</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Slot Settings Dialog */}
      <Dialog open={showSlotDialog} onOpenChange={setShowSlotDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Customize Daily Time Slots</DialogTitle>
            <DialogDescription>Rename the 3 daily chore slots and set their display times to match how your shelter operates.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveSlots} className="space-y-4">
            {slotForm.map((s, idx) => (
              <div key={s.slot} className="grid grid-cols-2 gap-3 items-end border-b pb-3">
                <div>
                  <label className="text-sm font-medium text-gray-700">Slot {idx + 1} name</label>
                  <Input value={s.label} onChange={(e) => setSlotForm(slotForm.map((x, i) => i === idx ? { ...x, label: e.target.value } : x))} data-testid={`slot-label-input-${s.slot}`} required />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Time</label>
                  <Input value={s.time} onChange={(e) => setSlotForm(slotForm.map((x, i) => i === idx ? { ...x, time: e.target.value } : x))} placeholder="e.g., 9:00 AM" data-testid={`slot-time-input-${s.slot}`} required />
                </div>
              </div>
            ))}
            <div className="flex justify-end space-x-3">
              <Button type="button" variant="outline" onClick={() => setShowSlotDialog(false)}>Cancel</Button>
              <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-slots-btn">Save Slots</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Clear All Dialog */}
      <Dialog open={showClearDialog} onOpenChange={setShowClearDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">Clear All Assignments</DialogTitle>
            <DialogDescription>Permanently delete all chores and laundry assignments? This cannot be undone.</DialogDescription>
          </DialogHeader>
          <div className="flex justify-end space-x-3 mt-4">
            <Button variant="outline" onClick={() => setShowClearDialog(false)} disabled={assignmentLoading}>Cancel</Button>
            <Button variant="destructive" onClick={confirmClearAllAssignments} disabled={assignmentLoading} className="bg-red-600 hover:bg-red-700" data-testid="confirm-clear-btn">
              {assignmentLoading ? 'Clearing...' : 'Clear All'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ChoreManagement;
